import type * as ThreeTypes from 'three';
import {
  IThumbnailGenerationService,
  IThumbnailGenerationConfig,
  IThumbnailGenerationDependencies
} from './types';
import { captureError } from '@/utils/sentry';

export class ThumbnailGenerationService implements IThumbnailGenerationService {
  private config: IThumbnailGenerationConfig;

  constructor(
    _dependencies: IThumbnailGenerationDependencies,
    config?: Partial<IThumbnailGenerationConfig>
  ) {
    this.config = {
      supportedFormats: ['.glb', '.gltf', '.fbx'],
      thumbnailSize: { width: 512, height: 512 },
      loadTimeout: 15000,
      renderDelay: 3000,
      ...config
    };
  }

  isModelPreviewSupported(url: string): boolean {
    if (url.startsWith('blob:')) return true;
    const extension = url.toLowerCase().split('.').pop();
    return this.config.supportedFormats.includes(`.${extension}`);
  }

  dataURLToBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  }

  async generateModelThumbnail(modelUrl: string, _fileName?: string, options?: { asMask?: boolean }): Promise<string> {
    if (!this.isModelPreviewSupported(modelUrl)) {
      throw new Error(`Unsupported format for thumbnail generation: ${modelUrl.split('.').pop()}`);
    }

    const THREE = await import('three');
    const { width, height } = this.config.thumbnailSize;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    renderer.debug.checkShaderErrors = import.meta.env.DEV;
    renderer.setPixelRatio(1);
    renderer.setSize(width, height, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 3));
    const key = new THREE.DirectionalLight(0xffffff, 1);
    key.position.set(5, 5, 5);
    scene.add(key);
    const fillA = new THREE.DirectionalLight(0xffffff, 0.4);
    fillA.position.set(-5, 0, 5);
    scene.add(fillA);
    const fillB = new THREE.DirectionalLight(0xffffff, 0.3);
    fillB.position.set(0, 5, -5);
    scene.add(fillB);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);

    const disposed: { renderer: ThreeTypes.WebGLRenderer; model?: ThreeTypes.Object3D } = { renderer };
    const cleanup = () => {
      if (disposed.model) {
        disposed.model.traverse((obj) => {
          const m = obj as ThreeTypes.Mesh;
          if (m.isMesh) {
            m.geometry?.dispose();
            const mat = m.material;
            if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
            else mat?.dispose();
          }
        });
      }
      disposed.renderer.dispose();
    };

    try {
      const [{ GLTFLoader }, { withModelDecoders }] = await Promise.all([
        import('three/examples/jsm/loaders/GLTFLoader.js'),
        import('@/modules/compressedModels/modelDecoders'),
      ]);
      const loader = withModelDecoders(new GLTFLoader(), renderer);
      const gltf = await new Promise<{ scene: ThreeTypes.Group }>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`Thumbnail GLB load timeout after ${this.config.loadTimeout}ms`)), this.config.loadTimeout);
        loader.load(
          modelUrl,
          (g) => { clearTimeout(timer); resolve(g as unknown as { scene: ThreeTypes.Group }); },
          undefined,
          (err) => { clearTimeout(timer); reject(err instanceof Error ? err : new Error(String(err))); },
        );
      });

      const model = gltf.scene;
      disposed.model = model;
      scene.add(model);

      // Frame mesh geometry only; Line/Points/path helpers would blow out the box.
      model.updateMatrixWorld(true);
      const box = new THREE.Box3();
      const _meshBox = new THREE.Box3();
      model.traverse((obj) => {
        const m = obj as ThreeTypes.Mesh & ThreeTypes.SkinnedMesh;
        if (!m.isMesh && !m.isSkinnedMesh) return;
        _meshBox.setFromObject(m);
        if (Number.isFinite(_meshBox.min.x) && Number.isFinite(_meshBox.max.x) && !_meshBox.isEmpty()) box.union(_meshBox);
      });
      if (box.isEmpty()) throw new Error('Thumbnail model has empty bounds');
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const objectWidth = size.x;
      const objectDepth = size.z;
      const objectHeight = size.y;
      const dimensionRatio = objectWidth / Math.max(objectDepth, 0.0001);
      const threshold = 1.2;
      let baseAngle: number;
      if (dimensionRatio < 1 / threshold) baseAngle = Math.PI / 2 - Math.PI / 6;
      else if (dimensionRatio > threshold) baseAngle = Math.PI / 6;
      else baseAngle = Math.PI / 4;

      const fovV = THREE.MathUtils.degToRad(camera.fov);
      const fovH = 2 * Math.atan(Math.tan(fovV / 2) * camera.aspect);
      const viewExtentX = objectWidth * Math.abs(Math.cos(baseAngle)) + objectDepth * Math.abs(Math.sin(baseAngle));
      const distV = (objectHeight * 0.5) / Math.tan(fovV / 2);
      const distH = (viewExtentX * 0.5) / Math.tan(fovH / 2);
      const dist = Math.max(distV, distH) * 1.25;

      camera.position.set(
        center.x + Math.sin(baseAngle) * dist,
        center.y,
        center.z + Math.cos(baseAngle) * dist,
      );
      camera.near = Math.max(0.01, dist / 100);
      camera.far = Math.max(1000, dist * 10);
      camera.lookAt(center);
      camera.updateProjectionMatrix();

      if (options?.asMask) {
        const { createMaskCheckerMaterial, maskCellForGeometry } = await import('@/modules/maskMaterial/maskCheckerMaterial');
        model.traverse((obj) => {
          const m = obj as ThreeTypes.Mesh;
          if (m.isMesh && m.geometry) m.material = createMaskCheckerMaterial(maskCellForGeometry(m.geometry as never)) as never;
        });
      }

      renderer.render(scene, camera);
      const dataURL = renderer.domElement.toDataURL('image/png');

      const BLANK_PNG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
      if (dataURL === BLANK_PNG) throw new Error('Thumbnail render produced blank canvas');

      cleanup();
      return dataURL;
    } catch (err) {
      cleanup();
      captureError(err, { domain: 'thumbnail', operation: 'generateModelThumbnail', modelUrl });
      throw err;
    }
  }
}
